/**
 * IELTS V2 — Listening Simulation Player
 * Route: /resources/ielts/listening/simulation
 *
 * Full-screen dedicated testing environment. Hides site navigation, footer,
 * and marketing chrome for the duration of the simulation. Phases:
 *   info      → Test Information / integrity acknowledgement
 *   in_test   → Part 1 → Part 2 → Part 3 → Part 4 (single continuous shell)
 *   confirm   → final submission confirmation
 *   submitting→ inline "marking your test" state
 *   complete  → redirect to /resources/ielts/results/:attempt_id
 *
 * Server-authoritative timer (30 min inside test). Autosave every 4s while
 * the student is typing, and immediately on part change / radio selection.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, Headphones, AlertTriangle, Clock, Save, CheckCircle2,
  ChevronRight, ChevronLeft, Play, Volume2, ShieldCheck, ListChecks,
} from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const ax = axios.create({ baseURL: BACKEND, withCredentials: true });
const AUTOSAVE_DEBOUNCE_MS = 1200;

function fmt(sec) {
  const s = Math.max(0, Number(sec) || 0);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, '0')}`;
}

export default function IeltsListeningSimulationPage() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [phase, setPhase] = useState('info'); // info | in_test | confirm | submitting | complete
  const [ack, setAck] = useState(false);
  const [state, setState] = useState(null); // attempt
  const [responses, setResponses] = useState({});
  const [currentPart, setCurrentPart] = useState(1);
  const [saving, setSaving] = useState('idle'); // idle | saving | saved | offline
  const [remaining, setRemaining] = useState(0);
  const [submitError, setSubmitError] = useState(null);

  const saveTimer = useRef(null);
  const pendingRef = useRef({});
  const remainingRef = useRef(0);

  useEffect(() => { remainingRef.current = remaining; }, [remaining]);

  // Prevent accidental navigation during a live test
  useEffect(() => {
    if (phase !== 'in_test') return;
    const before = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', before);
    return () => window.removeEventListener('beforeunload', before);
  }, [phase]);

  // Timer tick — 1s local decrement, resynced on autosave / periodic poll
  useEffect(() => {
    if (phase !== 'in_test') return;
    const t = setInterval(() => setRemaining((r) => Math.max(0, r - 1)), 1000);
    return () => clearInterval(t);
  }, [phase]);

  // Server clock resync every 20s
  useEffect(() => {
    if (phase !== 'in_test' || !state?.id) return;
    const t = setInterval(async () => {
      try {
        const r = await ax.get(`/api/ielts/v2/listening/simulation/state/${state.id}`);
        if (typeof r.data?.remaining_seconds === 'number') {
          setRemaining(r.data.remaining_seconds);
        }
      } catch { setSaving('offline'); }
    }, 20000);
    return () => clearInterval(t);
  }, [phase, state?.id]);

  // Auto-final-submit when timer reaches 0
  useEffect(() => {
    if (phase !== 'in_test' || remaining > 0) return;
    const t = setTimeout(() => setPhase('confirm'), 300);
    return () => clearTimeout(t);
  }, [phase, remaining]);

  const queueSave = useCallback((patch) => {
    pendingRef.current = { ...pendingRef.current, ...patch };
    setSaving('saving');
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      const payload = { responses: pendingRef.current, current_part: currentPart };
      pendingRef.current = {};
      try {
        const r = await ax.post(
          `/api/ielts/v2/listening/simulation/state/${state.id}/save-bulk`,
          payload,
        );
        if (typeof r.data?.remaining_seconds === 'number') {
          setRemaining(r.data.remaining_seconds);
        }
        setSaving('saved');
      } catch {
        setSaving('offline');
      }
    }, AUTOSAVE_DEBOUNCE_MS);
  }, [currentPart, state?.id]);

  // Start (or resume) simulation
  const startSimulation = useCallback(async () => {
    try {
      const r = await ax.post('/api/ielts/v2/listening/simulation/start', {
        variant: localStorage.getItem('ielts_variant') || 'academic',
      });
      const a = r.data || {};
      const stateResp = await ax.get(`/api/ielts/v2/listening/simulation/state/${a.id}`);
      const full = stateResp.data || {};
      setState(full);
      setResponses(full.responses || {});
      setCurrentPart(full.current_part || 1);
      setRemaining(full.remaining_seconds || 0);
      setPhase('in_test');
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Unable to start the simulation right now. Please try again in a moment.';
      if (e?.response?.status === 401) {
        toast.error('Please log in to start the IELTS Listening Simulation.');
        nav('/login?redirect=/resources/ielts/listening/simulation');
        return;
      }
      toast.error(msg);
    }
  }, [nav]);

  // Deep-link resume support
  useEffect(() => {
    if (params.get('resume') === '1') {
      (async () => {
        try {
          const r = await ax.get('/api/ielts/v2/listening/simulation/resume');
          if (r.data?.resumable) {
            const a = r.data.attempt;
            const stateResp = await ax.get(`/api/ielts/v2/listening/simulation/state/${a.id}`);
            const full = stateResp.data || {};
            setState(full);
            setResponses(full.responses || {});
            setCurrentPart(full.current_part || 1);
            setRemaining(full.remaining_seconds || 0);
            setPhase('in_test');
          }
        } catch {}
      })();
    }
  }, [params]);

  const setAnswer = (qid, value) => {
    setResponses((prev) => ({ ...prev, [qid]: value }));
    queueSave({ [qid]: value });
  };

  const goToPart = (n) => {
    setCurrentPart(n);
    queueSave({});
    setTimeout(() => {
      const el = document.getElementById('ielts-sim-part-top');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 30);
  };

  const finalSubmit = async () => {
    setPhase('submitting');
    setSubmitError(null);
    // Flush any pending saves synchronously first
    try {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      if (Object.keys(pendingRef.current).length) {
        await ax.post(`/api/ielts/v2/listening/simulation/state/${state.id}/save-bulk`, {
          responses: pendingRef.current, current_part: currentPart,
        });
        pendingRef.current = {};
      }
      const r = await ax.post(`/api/ielts/v2/listening/simulation/state/${state.id}/complete`);
      if (r.data?.attempt_id) {
        setPhase('complete');
        // Mock-mode redirect: no result leakage during active mock.
        let mockCtx = null;
        try { mockCtx = JSON.parse(localStorage.getItem('ielts_mock_context') || 'null'); } catch { mockCtx = null; }
        if (mockCtx?.mockId && mockCtx.section === 'listening') {
          nav(`/resources/ielts/mock/session/${mockCtx.mockId}`, { replace: true });
        } else {
          nav(`/resources/ielts/results/${r.data.attempt_id}`, { replace: true });
        }
      } else {
        throw new Error('Missing attempt id');
      }
    } catch (e) {
      setSubmitError(e?.response?.data?.detail || 'We could not submit your test. Please try again in a moment.');
      setPhase('in_test');
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-slate-950 text-slate-100 overflow-y-auto"
       data-testid="ielts-sim-shell">
      {phase === 'info' && (
        <TestInfo onBegin={startSimulation} ack={ack} setAck={setAck} onExit={() => nav('/resources/ielts')} />
      )}
      {phase === 'in_test' && state && (
        <InTest state={state} responses={responses} setAnswer={setAnswer}
          currentPart={currentPart} goToPart={goToPart}
          remaining={remaining} saving={saving}
          onFinishClick={() => setPhase('confirm')} />
      )}
      {phase === 'confirm' && (
        <ConfirmSubmit responses={responses} totalQuestions={40}
          onCancel={() => setPhase('in_test')} onConfirm={finalSubmit} timeUp={remaining <= 0} />
      )}
      {phase === 'submitting' && (
        <CenteredCard>
          <Loader2 className="h-8 w-8 animate-spin text-indigo-400 mb-4" />
          <div className="text-xl font-bold">Marking your test…</div>
          <div className="text-sm text-slate-400 mt-2">Please wait — this usually takes just a few seconds.</div>
          {submitError && <div className="mt-4 text-rose-400 text-sm">{submitError}</div>}
        </CenteredCard>
      )}
      {phase === 'complete' && (
        <CenteredCard>
          <CheckCircle2 className="h-10 w-10 text-emerald-400 mb-3" />
          <div className="text-xl font-bold">Redirecting to your results…</div>
        </CenteredCard>
      )}
    </div>
  );
}

/* ----------------------------------------------------------------------- */
function CenteredCard({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <div className="text-center max-w-md">{children}</div>
    </div>
  );
}

function TestInfo({ onBegin, ack, setAck, onExit }) {
  return (
    <div className="max-w-3xl mx-auto px-5 md:px-10 py-10 md:py-16">
      <div className="text-[11px] uppercase tracking-widest text-indigo-300 font-bold mb-3">
        Winhouse IELTS · Full Simulation
      </div>
      <h1 className="text-3xl md:text-5xl font-black">
        IELTS <span className="text-indigo-400">Listening</span> Simulation
      </h1>
      <p className="text-slate-300 mt-3 text-sm md:text-base">
        You are about to enter a dedicated Winhouse testing environment. Please
        complete the test in one uninterrupted sitting for the most accurate
        practice band estimate.
      </p>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
        <InfoCard icon={<ListChecks className="h-5 w-5" />} title="Test format" body="4 Parts · 10 questions each · 40 questions total. Audio for each part plays automatically inside its section." />
        <InfoCard icon={<Clock className="h-5 w-5" />} title="Timing" body="Approximately 30 minutes of audio + 2 minutes to review. The timer is server-controlled and cannot be paused." />
        <InfoCard icon={<Headphones className="h-5 w-5" />} title="Before you begin" body="Use headphones in a quiet environment. Check your audio output level using the sample player in Part 1." />
        <InfoCard icon={<ShieldCheck className="h-5 w-5" />} title="Test integrity" body="No transcripts, hints or answer keys are shown during the test. Your work is autosaved and can be resumed if you disconnect." />
      </div>

      <div className="mt-6 rounded-lg bg-amber-950/40 border border-amber-700/40 p-4 text-sm text-amber-200 flex gap-3">
        <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
        <div>
          Once you begin, the timer starts and cannot be stopped. Closing the tab
          will preserve your progress but the timer will continue to run.
        </div>
      </div>

      <label className="mt-6 flex items-start gap-3 text-sm cursor-pointer select-none">
        <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)}
          data-testid="ielts-sim-ack" className="mt-1 h-4 w-4 accent-indigo-500" />
        <span>
          I understand this is a Winhouse Estimated IELTS Practice Band and not an
          official IELTS score. Winhouse Tutors is not affiliated with IDP, British
          Council or Cambridge Assessment English.
        </span>
      </label>

      <div className="mt-8 flex items-center gap-3">
        <button onClick={onBegin} disabled={!ack}
          data-testid="ielts-sim-begin"
          className="px-6 py-3 rounded-full bg-indigo-500 hover:bg-indigo-400 disabled:opacity-40 text-white font-bold inline-flex items-center gap-2 transition">
          <Play className="h-4 w-4" /> Begin Simulation
        </button>
        <button onClick={onExit} className="px-4 py-2 text-slate-400 hover:text-slate-100 text-sm">
          Return to IELTS Hub
        </button>
      </div>
    </div>
  );
}

function InfoCard({ icon, title, body }) {
  return (
    <div className="rounded-xl bg-slate-900/60 border border-slate-700/40 p-4">
      <div className="flex items-center gap-2 text-indigo-300 font-bold text-sm">{icon}{title}</div>
      <div className="mt-2 text-sm text-slate-300 leading-relaxed">{body}</div>
    </div>
  );
}

/* ----------------------------------------------------------------------- */
function InTest({ state, responses, setAnswer, currentPart, goToPart, remaining, saving, onFinishClick }) {
  const parts = state.parts || [];
  const part = parts.find((p) => p.part_number === currentPart) || parts[0];
  const answered = useMemo(() => {
    return parts.reduce((acc, p) => {
      const c = (p.questions || []).filter((q) => responses[q.id] !== undefined && responses[q.id] !== '').length;
      acc[p.part_number] = c;
      return acc;
    }, {});
  }, [parts, responses]);
  if (!part) return <CenteredCard><Loader2 className="h-6 w-6 animate-spin" /></CenteredCard>;

  return (
    <div className="min-h-screen flex flex-col">
      {/* Fixed header */}
      <div className="sticky top-0 z-10 bg-slate-900/95 backdrop-blur border-b border-slate-700/40 px-4 md:px-8 py-3 flex items-center gap-3 md:gap-6">
        <div className="text-[10px] md:text-xs font-black tracking-widest text-indigo-300">WINHOUSE · IELTS LISTENING</div>
        <div className="hidden md:flex items-center gap-1.5 text-xs text-slate-300">
          <span className="text-slate-500">Part {currentPart} / 4</span>
        </div>
        <div className="ml-auto flex items-center gap-4">
          <div className="flex items-center gap-1.5 text-xs text-slate-300">
            <Save className="h-3.5 w-3.5" />
            <span data-testid="ielts-sim-save-chip"
              className={saving === 'offline' ? 'text-rose-400' : (saving === 'saving' ? 'text-amber-300' : 'text-emerald-300')}>
              {saving === 'saving' ? 'Saving…' : saving === 'offline' ? 'Reconnecting…' : 'Saved'}
            </span>
          </div>
          <div className="flex items-center gap-1.5 rounded-full bg-slate-800 px-3 py-1.5 text-sm font-bold tabular-nums"
             data-testid="ielts-sim-timer">
            <Clock className="h-3.5 w-3.5 text-indigo-300" />
            {fmt(remaining)}
          </div>
        </div>
      </div>

      {/* Body */}
      <div id="ielts-sim-part-top" className="max-w-5xl w-full mx-auto px-4 md:px-8 py-6 md:py-8 flex-1">
        <PartHeader part={part} answered={answered[currentPart] || 0} />
        <PartQuestions part={part} responses={responses} setAnswer={setAnswer} />
        <PartNav parts={parts} currentPart={currentPart} answered={answered} onGoto={goToPart} onFinish={onFinishClick} />
      </div>
    </div>
  );
}

function PartHeader({ part, answered }) {
  return (
    <div className="rounded-xl bg-slate-900/60 border border-slate-700/40 p-4 md:p-5" data-testid={`ielts-sim-part-${part.part_number}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-indigo-300 font-bold">Part {part.part_number} of 4</div>
          <div className="text-lg md:text-2xl font-black">{part.title}</div>
        </div>
        <div className="text-xs text-slate-400">
          <span className="tabular-nums font-bold text-slate-100">{answered}</span> / {part.questions.length} answered
        </div>
      </div>
      {part.context && <p className="text-sm text-slate-300 mt-2">{part.context}</p>}
      {part.instructions && (
        <div className="mt-3 text-[11px] uppercase tracking-widest font-bold text-amber-300">
          {part.instructions}
        </div>
      )}
      <div className="mt-3 flex items-center gap-2 text-xs text-slate-400">
        <Volume2 className="h-3.5 w-3.5" />
        Listen carefully. Audio may play only within this part.
      </div>
      <audio controls preload="metadata" className="mt-2 w-full"
        data-testid={`ielts-sim-audio-part-${part.part_number}`}>
        <source src={`${BACKEND}${part.audio_url}`} />
      </audio>
    </div>
  );
}

function PartQuestions({ part, responses, setAnswer }) {
  return (
    <div className="mt-5 space-y-3">
      {part.questions.map((q) => (
        <div key={q.id} className="rounded-lg bg-slate-900/40 border border-slate-700/40 p-4"
          data-testid={`ielts-sim-q-${q.id}`}>
          <div className="text-sm font-semibold text-slate-100">
            <span className="text-indigo-300 mr-2">{q.number}.</span> {q.prompt}
          </div>
          {q.options ? (
            <div className="mt-3 space-y-1.5">
              {q.options.map((opt) => {
                const letter = opt.split('.')[0].trim();
                return (
                  <label key={opt} className="flex items-center gap-2 text-sm cursor-pointer hover:text-white text-slate-300">
                    <input type="radio" name={q.id} value={letter}
                      checked={responses[q.id] === letter}
                      onChange={() => setAnswer(q.id, letter)}
                      data-testid={`ielts-sim-q-${q.id}-opt-${letter}`}
                      className="accent-indigo-500" />
                    {opt}
                  </label>
                );
              })}
            </div>
          ) : (
            <input type="text"
              className="mt-3 w-full bg-slate-950/50 border border-slate-700 focus:border-indigo-400 rounded px-3 py-2 text-sm text-slate-100 placeholder-slate-500 outline-none"
              placeholder={q.word_limit ? `Type your answer (max ${q.word_limit} words)` : 'Your answer'}
              value={responses[q.id] || ''}
              onChange={(e) => setAnswer(q.id, e.target.value)}
              data-testid={`ielts-sim-q-${q.id}-input`} />
          )}
          {q.word_limit && (
            <div className="mt-1 text-[10px] text-slate-500">
              Word limit: {q.word_limit}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function PartNav({ parts, currentPart, answered, onGoto, onFinish }) {
  const total = parts.reduce((s, p) => s + p.questions.length, 0);
  const totalAnswered = Object.values(answered).reduce((a, b) => a + b, 0);
  return (
    <div className="mt-6 rounded-xl bg-slate-900/70 border border-slate-700/40 p-4 flex items-center gap-3 flex-wrap sticky bottom-3">
      <button
        onClick={() => onGoto(Math.max(1, currentPart - 1))}
        disabled={currentPart === 1}
        data-testid="ielts-sim-prev-part"
        className="px-3 py-1.5 rounded-full bg-slate-800 hover:bg-slate-700 disabled:opacity-30 text-xs font-bold text-slate-100 inline-flex items-center gap-1">
        <ChevronLeft className="h-3.5 w-3.5" /> Previous Part
      </button>
      <div className="flex items-center gap-1.5">
        {parts.map((p) => (
          <button key={p.part_number} onClick={() => onGoto(p.part_number)}
            data-testid={`ielts-sim-goto-part-${p.part_number}`}
            className={`px-3 py-1.5 rounded-full text-xs font-bold border transition ${
              p.part_number === currentPart
                ? 'bg-indigo-500 border-indigo-500 text-white'
                : 'bg-slate-800 border-slate-700 text-slate-300 hover:border-indigo-400'}`}>
            Part {p.part_number} <span className="text-[10px] opacity-70">· {answered[p.part_number] || 0}/{p.questions.length}</span>
          </button>
        ))}
      </div>
      {currentPart < 4 ? (
        <button
          onClick={() => onGoto(currentPart + 1)}
          data-testid="ielts-sim-next-part"
          className="ml-auto px-4 py-1.5 rounded-full bg-slate-800 hover:bg-slate-700 text-xs font-bold text-slate-100 inline-flex items-center gap-1">
          Next Part <ChevronRight className="h-3.5 w-3.5" />
        </button>
      ) : (
        <button
          onClick={onFinish}
          data-testid="ielts-sim-finish"
          className="ml-auto px-5 py-2 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-black inline-flex items-center gap-1.5">
          Finish &amp; Submit <ChevronRight className="h-4 w-4" />
          <span className="ml-1 text-xs text-emerald-950 font-bold">({totalAnswered}/{total})</span>
        </button>
      )}
    </div>
  );
}

function ConfirmSubmit({ responses, totalQuestions, onCancel, onConfirm, timeUp }) {
  const answered = Object.keys(responses).filter((k) => responses[k] !== undefined && responses[k] !== '').length;
  const unanswered = totalQuestions - answered;
  return (
    <div className="min-h-screen flex items-center justify-center px-5">
      <div className="max-w-lg w-full rounded-2xl bg-slate-900 border border-slate-700 p-6 md:p-8">
        <div className="text-xs uppercase tracking-widest text-indigo-300 font-bold">Final submission</div>
        <h2 className="text-2xl font-black mt-1">Submit your Listening test?</h2>
        {timeUp && (
          <div className="mt-3 text-sm text-amber-300 flex items-center gap-2">
            <Clock className="h-4 w-4" /> The section time has ended — please submit to see your results.
          </div>
        )}
        <div className="mt-4 space-y-2 text-sm">
          <div className="flex items-baseline gap-2">
            <span className="text-slate-400">Answered:</span>
            <span className="font-bold text-emerald-300 tabular-nums">{answered} / {totalQuestions}</span>
          </div>
          {unanswered > 0 && (
            <div className="flex items-baseline gap-2">
              <span className="text-slate-400">Unanswered:</span>
              <span className="font-bold text-amber-300 tabular-nums">{unanswered}</span>
            </div>
          )}
        </div>
        <div className="mt-4 text-xs text-slate-400 leading-relaxed">
          Once submitted, your test will be marked instantly. Your attempt is
          permanently preserved — you can retry from the results screen and every
          new attempt is kept separately.
        </div>
        <div className="mt-6 flex items-center gap-3">
          <button onClick={onCancel} data-testid="ielts-sim-confirm-cancel"
            className="px-4 py-2 rounded-full bg-slate-800 hover:bg-slate-700 text-sm text-slate-100">
            Back to test
          </button>
          <button onClick={onConfirm} data-testid="ielts-sim-confirm-submit"
            className="ml-auto px-5 py-2 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-black">
            Submit test
          </button>
        </div>
      </div>
    </div>
  );
}
